/**
 * The small lines under a dashboard tile's figure (SF-5117): a per-day figure, a trend, and the
 * new and gone quiet counts under active companies.
 */
import { Button, Space, Typography, theme } from 'antd';
import type { PlatformUsage } from '../../../components/usage/usageClient';
import { isInProgress } from '../../../components/usage/usageProgress';
import { formatNumber } from '../../../utils/format';

const { Text } = Typography;

/** The lines under a tile's figure, one per row; empty lines are left out. */
export const TileLines: React.FC<{ lines: React.ReactNode[] }> = ({ lines }) => {
  const shown = lines.filter((l) => l !== null && l !== undefined && l !== false && l !== '');
  if (shown.length === 0) return null;
  return (
    <Space direction="vertical" size={0} style={{ display: 'flex', fontSize: 13, marginTop: 4 }}>
      {shown.map((line, i) => (
        <div key={i}>{typeof line === 'string' ? <Text type="secondary">{line}</Text> : line}</div>
      ))}
    </Space>
  );
};

/**
 * "+2 new" (green) and "−3 gone quiet" (red), or "−3 no feedback yet" in amber while the month
 * is in progress, since those companies may still send feedback. Each opens the Changes list,
 * when the dashboard has one.
 * Nothing when an older API sends neither count.
 */
export const NewGone: React.FC<{ data: PlatformUsage; onShowChanges?: () => void }> = ({
  data,
  onShowChanges,
}) => {
  const { token } = theme.useToken();
  const { new: added, gone } = data.active_companies;
  if (typeof added !== 'number' && typeof gone !== 'number') return null;
  const inProgress = isInProgress(data.progress);
  const link: React.CSSProperties = { padding: 0, height: 'auto' };
  // A count opens the Changes list when there is one; otherwise (an older API) it is plain text.
  const count = (text: string, color: string) =>
    onShowChanges ? (
      <Button type="link" size="small" style={{ ...link, color }} onClick={onShowChanges}>
        {text}
      </Button>
    ) : (
      <Text style={{ color }}>{text}</Text>
    );
  return (
    <Space size="small" wrap>
      {typeof added === 'number' && count(`+${formatNumber(added)} new`, token.colorSuccess)}
      {typeof gone === 'number' &&
        count(
          `\u2212${formatNumber(gone)} ${inProgress ? 'no feedback yet' : 'gone quiet'}`,
          inProgress ? token.colorWarning : token.colorError,
        )}
    </Space>
  );
};
